//
// Copyright (c) 2020 Astute Global, Inc.
//

define([
  "backbone",
  "jquery",
  "underscore",
  "messaging/messenger",
  "configuration/properties",
  "modules/shared/sortableList/sortableListModel",
  "modules/shared/history/historyModel",
], function (Backbone, $, _, Messenger, Properties, SortableListModel, HistoryModel) {
  "use strict";

  const DepartmentModel = {};

  DepartmentModel.departmentUser = Backbone.Model.extend({
    defaults: {
      departmentId: 0,
      languageId: 0,
      userId: 0,
    },
    parse: function (response) {
      const department = response.department || {};
      const language = response.language || {};
      const user = response.user || {};
      response.departmentName = department.name || response.departmentId;
      response.languageName = language.name || response.languageId;
      response.userName = user.userName || response.userId;
      response.userLoginId = user.userLoginId || response.userId;
      return response;
    },
  });

  DepartmentModel.departmentUsers = Backbone.AtomicCollection.extend({
    model: DepartmentModel.departmentUser,
    url: function () {
      if (!this.department) {
        if (this.collection) {
          this.department = this.collection.department;
        }
      }
      if (!this.department || this.department.isNew()) {
        return "";
      }
      const baseUrl = this.department.url();
      if (baseUrl.endsWith("/")) {
        return baseUrl + "users";
      }
      return baseUrl + "/users";
    },
    addUser: function (userId, kbId, languageId) {
      const existingUser = this.findWhere({
        kbId: kbId,
        languageId: languageId,
        userId: userId,
      });
      if (!existingUser) {
        const departmentId = this.department.id;
        this.add(
          new DepartmentModel.departmentUser({
            departmentId: departmentId,
            kbId: kbId,
            languageId: languageId,
            userId: userId,
          }),
        );
        this.department.dirty = true;
      }
    },
    backup: function () {
      //backup the collection into a backup variable once the collection is synced
      this.onSynced(function () {
        this._backup();
      }, this);
    },
    removeUser: function (userId, languageId) {
      const existingUser = this.findWhere({
        languageId: languageId,
        userId: userId,
      });
      if (existingUser) {
        this.remove(existingUser);
        this.department.dirty = true;
      }
    },
    restore: function () {
      //restore backup by resetting the collection using the backup's models
      this.reset(this.backupCol.models);
      //set the dirty flag back to what it was before the backup
      this.dirty = this.wasDirty;
    },
    _backup: function () {
      //save whether the example was dirty at the time
      this.wasDirty = this.dirty;
      //create a new collection and add cloned models to that collection
      const col = new DepartmentModel.departmentUsers();
      this.each(function (model) {
        col.add(model.clone());
      });
      this.backupCol = col;
    },
  });

  DepartmentModel.department = Backbone.Model.extend({
    defaults: {
      lastUpdateUserId: 0,
      lastUpdateUserName: "",
      createUserId: 0,
      createUserName: "",
      departmentUsers: [],
      description: "",
      groupId: 0,
      name: "",
      taskDescription: "",
      userCount: 0,
    },
    url: function () {
      if (!this.tenantId) {
        if (this.collection) {
          this.tenantId = this.collection.tenantId;
        }
        if (!this.tenantId) {
          this.tenantId = Messenger.reqres.request("active:tenantId");
          if (!this.tenantId) {
            return "";
          }
        }
      }
      let url = Properties.adminRootURL + "/tenants/" + this.tenantId + "/departments";
      const id = this.get("id");
      if (id) {
        url += "/" + id;
      }
      return url;
    },
    backup: function () {
      //backup the model into a backup variable once the model is synced
      this._backup();
      this.onSynced(function () {
        this._backup();
      }, this);
    },
    parse: function (resp) {
      if (resp.departmentUsers) {
        this.usersCol = new DepartmentModel.departmentUsers(resp.departmentUsers, { parse: true });
        this.usersCol.department = this;
        delete resp.departmentUsers;
      }
      return resp;
    },
    restore: function () {
      //restore backup by resetting the model using the backup's attributes
      this.set(this.clonedCopy.attributes);
      _.each(
        this.subModels,
        function (subModel) {
          subModel.restore();
        },
        this,
      );
      //set the dirty flag back to what it was before the backup
      this.dirty = this.wasDirty;
    },
    history: function () {
      const history = new HistoryModel.historyCollection();
      if (!this.isNew()) {
        const url = this.url() + "/history";
        history.url = function () {
          return url;
        };
        history.fetch();
      }
      return history;
    },
    users: function () {
      this.usersCol = this.getSubModel(this.usersCol, DepartmentModel.departmentUsers, "/users");
      this.usersCol.department = this;
      return this.usersCol;
    },
    saveChildren: function (options) {
      this.saveSubModels(options);
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "name") {
        return (
          this.validateRequiredValue(value, {
            emptyValueMessage: "admin.department.noName",
            maxLength: 255,
          }) || this.validateSafeCharacters(value)
        );
      }
    },
    _backup: function () {
      //save whether the example was dirty at the time
      this.wasDirty = this.dirty;
      //create a new cloned model
      this.clonedCopy = this.clone();
      _.each(
        this.subModels,
        function (subModel) {
          subModel.backup();
        },
        this,
      );
    },
  });

  DepartmentModel.departments = Backbone.Collection.extend({
    model: DepartmentModel.department,
    url: function () {
      if (!this.tenantId) {
        this.tenantId = Messenger.reqres.request("active:tenantId");
        if (!this.tenantId) {
          return "";
        }
      }
      let url = Properties.adminRootURL + "/tenants/" + this.tenantId + "/departments";
      if (this.includeUsers) {
        url += "?includeUsers=true";
      }
      return url;
    },
    addDepartment: function () {
      const currentUser = Messenger.reqres.request("user");
      const department = new DepartmentModel.department({
        lastUpdateDate: new Date(),
        lastUpdateUserId: currentUser.get("userId"),
        lastUpdateUserName: currentUser.get("userName"),
        createDate: new Date(),
        createUserId: currentUser.get("userId"),
        createUserName: currentUser.get("userName"),
        departmentUsers: [],
        description: "",
        groupId: 0,
        name: "",
        taskDescription: "",
        userCount: 0,
      });
      department.tenantId = this.tenantId;
      this.add(department);
      return department;
    },
  });

  Messenger.reqres.setHandler("department:list:headers", function () {
    const Header = SortableListModel.headerModel;
    return new Backbone.Collection([
      new Header({
        headerName: $.t("admin.department.name"),
        modelAttribute: "name",
        selectable: true,
        sortable: true,
        longTextField: true,
        filterable: true,
      }),
      new Header({
        headerName: $.t("admin.department.description"),
        modelAttribute: "description",
        selectable: false,
        sortable: true,
        longTextField: true,
        filterable: true,
        hideable: true,
      }),
      new Header({
        headerName: $.t("admin.department.taskDescription"),
        modelAttribute: "taskDescription",
        selectable: false,
        sortable: true,
        longTextField: true,
        filterable: true,
        hideable: true,
      }),
      new Header({
        headerName: $.t("admin.department.userCount"),
        modelAttribute: "userCount",
        selectable: false,
        sortable: true,
        numberField: true,
        hidden: true,
        hideable: true,
      }),
      new Header({
        headerName: $.t("admin.department.createDate"),
        modelAttribute: "createDate",
        dateField: true,
        dateOnly: true,
        selectable: false,
        sortable: true,
        hidden: true,
        hideable: true,
      }),
      new Header({
        headerName: $.t("admin.department.createUserName"),
        modelAttribute: "createUserName",
        selectable: false,
        sortable: true,
        hidden: true,
        hideable: true,
        filterable: true,
        textField: true,
      }),
      new Header({
        headerName: $.t("admin.department.lastUpdateDate"),
        modelAttribute: "lastUpdateDate",
        dateField: true,
        dateOnly: true,
        selectable: false,
        sortable: true,
        hidden: true,
        hideable: true,
      }),
      new Header({
        headerName: $.t("admin.department.lastUpdateUserName"),
        modelAttribute: "lastUpdateUserName",
        selectable: false,
        sortable: true,
        hidden: true,
        hideable: true,
        filterable: true,
        textField: true,
      }),
      new Header({
        headerName: "",
        modelAttribute: "name",
        width: "15px",
        customHtml:
          '<button type="button" class="btn btn-danger btn-mini deleteDepartment"\
             data-i18n="[title]admin.department.deleteDepartment" \
             data-toggle="tooltip"><i class="icon-remove"></i></button>',
      }),
    ]);
  });

  //Request for the headers of the topic examples history list
  Messenger.reqres.setHandler("department:history:headers", function (headerType) {
    const Header = SortableListModel.headerModel;
    if (headerType === "detailModal") {
      return new Backbone.Collection([
        new Header({
          headerName: $.t("history.oldValue"),
          modelAttribute: "oldValue",
          textField: true,
        }),
        new Header({
          headerName: $.t("history.newValue"),
          modelAttribute: "newValue",
          textField: true,
        }),
      ]);
    } else {
      return new Backbone.Collection([
        new Header({
          headerName: $.t("history.user"),
          modelAttribute: "userName",
          sortable: true,
          filterable: true,
          textField: true,
          width: "15%",
        }),
        new Header({
          headerName: $.t("history.date"),
          modelAttribute: "dateTime",
          defaultSort: true,
          defaultReversed: true,
          sortable: true,
          dateField: true,
        }),
        new Header({
          headerName: $.t("history.change"),
          modelAttribute: "changeType",
          sortable: true,
          filterable: true,
          textField: true,
          width: "15%",
        }),
        new Header({
          headerName: $.t("history.oldValue"),
          modelAttribute: "oldValue",
          sortable: true,
          textField: true,
        }),
        new Header({
          headerName: $.t("history.newValue"),
          modelAttribute: "newValue",
          sortable: true,
          textField: true,
        }),
      ]);
    }
  });

  const tenantDepartmentsMap = {};
  Messenger.reqres.setHandler("tenant:departments", function (options) {
    options = options || {};
    const tenantId = options.tenantId;
    if (!tenantId) {
      return null;
    }
    let tenantDepartments = tenantDepartmentsMap[tenantId];
    if (!tenantDepartments) {
      tenantDepartments = new DepartmentModel.departments();
      tenantDepartments.tenantId = tenantId;
      let url;
      url = Properties.adminRootURL + "/tenants/" + tenantId + "/departments";
      if (options.includeUsers) {
        url += "?includeUsers=true";
      }
      tenantDepartments.url = url;
      tenantDepartments.fetch();
      tenantDepartmentsMap[tenantId] = tenantDepartments;
    }
    return tenantDepartments;
  });

  const kbDepartmentsMap = {};
  Messenger.reqres.setHandler("kb:departments", function (options) {
    options = options || {};
    const kbId = options.kbId;
    if (!kbId) {
      return null;
    }
    let kbDepartments = kbDepartmentsMap[kbId];
    if (!kbDepartments) {
      kbDepartments = new DepartmentModel.departments();
      kbDepartments.kbId = kbId;
      let url = Properties.rootURL + "/" + kbId + "/departments";
      if (options.includeUsers) {
        url += "?includeUsers=true";
      }
      kbDepartments.url = url;
      kbDepartments.fetch();
      kbDepartmentsMap[kbId] = kbDepartments;
    }
    return kbDepartments;
  });

  return DepartmentModel;
});
